import React from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import palette from "../../common/styles/palette";

/**
 * A sobreposição de trabalho — o cadeado visível das abas da Entrevista.
 *
 * O DEFEITO QUE ELA CORRIGE. Enquanto o Copiloto gerava (dezenas de segundos),
 * a única pista na tela era o texto «Gerando o Checklist…» DENTRO do widget de
 * chat — que fica no canto, pode estar minimizado e não é para onde o professor
 * olha. Os campos ficavam desabilitados um a um (`disabled={busy}`), o que só
 * produz um cinza mudo: quem clicava concluía que a tela havia travado, e quem
 * estava digitando perdia o texto quando a resposta do modelo redesenhava o
 * Rascunho por baixo.
 *
 * O que ela faz é dizer as três coisas que faltavam, no lugar onde a edição
 * acontece: (1) alguém está trabalhando — o anel gira; (2) o que está sendo
 * feito — `titulo` e `detalhe` são as MESMAS frases do chat, para a tela não
 * contar duas histórias; (3) por que não dá para editar agora — a camada
 * intercepta o ponteiro, então o clique não «não faz nada», ele bate em algo.
 *
 * Bloquear o ponteiro aqui é a trava de verdade. `disabled` protege campo por
 * campo e esquece o que for acrescentado depois; a camada cobre a área inteira,
 * inclusive o que ainda não existe.
 *
 * `ativo` falso devolve `null` em vez de uma transição de saída: um `Fade` com
 * `unmountOnExit` deixa o nó vivo durante a saída, e nesse intervalo ele exibe
 * as frases do próximo estado — a camada terminava uma geração piscando
 * «Salvando…». A entrada continua animada, pelo keyframe abaixo.
 *
 * Não decide nada: `ativo` vem de quem conhece a espera (`gerando`, `adotando`).
 * `role="status"` + `aria-live="polite"` faz o leitor de tela anunciar a espera
 * uma vez, sem repetir a cada quadro da animação.
 */
interface Props {
  /** Se a camada está no ar. Falso a desmonta — nada de camada invisível. */
  ativo: boolean;
  /** A frase curta: o que está acontecendo agora. */
  titulo: string;
  /** A frase longa opcional: por que a espera vale a pena / quanto dura. */
  detalhe?: string;
}

export default function SobreposicaoTrabalho({
  ativo,
  titulo,
  detalhe,
}: Props) {
  if (!ativo) return null;

  return (
    <Box
      data-testid="interview-overlay-trabalho"
      role="status"
      aria-live="polite"
      aria-busy
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        px: 3,
        textAlign: "center",
        // Translúcido de propósito: o professor continua vendo QUAL aba está
        // sendo reescrita por baixo. Opaco seria uma tela de carregamento
        // genérica e perderia esse contexto.
        bgcolor: "rgba(249, 250, 253, 0.78)",
        backdropFilter: "blur(2px)",
        cursor: "progress",
        "@keyframes surgirCamada": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        animation: "surgirCamada .18s ease-out",
      }}
    >
      <Box sx={{ position: "relative", display: "flex" }}>
        {/* Anel de fundo + halo pulsando: a mesma espera contada duas vezes,
            para ler como «vivo» mesmo a 40cm de distância da tela. */}
        <CircularProgress
          size={44}
          thickness={4}
          sx={{ color: palette.brand }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: -10,
            borderRadius: "50%",
            border: `2px solid ${palette.brandSoft}`,
            "@keyframes halo": {
              "0%": { transform: "scale(0.82)", opacity: 0.9 },
              "100%": { transform: "scale(1.18)", opacity: 0 },
            },
            animation: "halo 1.6s ease-out infinite",
          }}
        />
      </Box>

      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, color: palette.brandDark }}
      >
        {titulo}
      </Typography>

      {detalhe && (
        <Typography
          variant="caption"
          sx={{ color: palette.textMuted, maxWidth: 420 }}
        >
          {detalhe}
        </Typography>
      )}
    </Box>
  );
}
